import { createContext, useState, useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { LoaderOverlay } from "./BouncingDots";
import { deferLoaderOnNavigation } from "./ssoLoaderDefer";

const ProgressContext = createContext(null);

export default ProgressContext;

// How long the overlay lingers after the last loader releases. A multi-step hand-off (one step's
// finishLoading and the next step's startLoading, or a lazy chunk swapping in for its Suspense
// fallback) lands a tick or two apart; without this the overlay unmounts and remounts in between,
// flashing the intermediate page. Any new start inside the window keeps the same overlay up.
const HIDE_DELAY_MS = 250;

export function ProgressContextProvider({ children }) {
    const [loader, setLoader] = useState(false);
    const [loaderText, setLoaderText] = useState("Loading...");

    const [loadingCount, setLoadingCount] = useState(0);
    const [shown, setShown] = useState(false);
    const location = useLocation();

    const startLoading = () => {
        setLoadingCount((count) => count + 1);
    };

    const finishLoading = () => {
        setLoadingCount((count) => Math.max(count - 1, 0));
    };

    const isLoading = loadingCount > 0;

    const showProg = (value) => {
        setLoader(value);
    };

    const wantVisible = loader || isLoading;

    // Show in the same render the first loader starts (no frame without the overlay).
    if (wantVisible && !shown) setShown(true);

    useEffect(() => {
        if (wantVisible || !shown) return;
        const timer = setTimeout(() => setShown(false), HIDE_DELAY_MS);
        return () => clearTimeout(timer);
    }, [wantVisible, shown]);

    // SSO session, CP intake flow (/service) only: navigation keeps the loader hidden for its first
    // second (ssoLoaderDefer.js checks the path, so other pages are untouched). A loader already up
    // (save/submit) is left visible. Layout effect so the class is on before the new route's loader
    // can paint. The first page load (and so a reload) is skipped.
    const shownRef = useRef(shown);
    shownRef.current = shown;
    const navKey = `${location.pathname}${location.search}`;
    const prevNavKey = useRef(navKey);
    useLayoutEffect(() => {
        if (prevNavKey.current === navKey) return;
        prevNavKey.current = navKey;
        deferLoaderOnNavigation(shownRef.current);
    }, [navKey]);

    // Intake steps move by replaceState (updateSearchParams "current"), which useLocation never sees.
    useEffect(() => {
        const onParamsChange = (e) => {
            if (e.detail?.name === "current") deferLoaderOnNavigation(shownRef.current);
        };
        window.addEventListener("searchparamschange", onParamsChange);
        return () => window.removeEventListener("searchparamschange", onParamsChange);
    }, []);

    useEffect(() => {

        if (location.pathname === '/security/logout')
            setLoaderText("");
        else
            setLoaderText("Loading...");

    }, [location]);


    return (
        <ProgressContext.Provider
            value={{ progParams: showProg, startLoading, finishLoading, isProgressVisible: shown }}
        >
            {children}
            {shown && <LoaderOverlay text={loaderText} />}
        </ProgressContext.Provider>
    );
}
